import { replace } from 'connected-react-router';
import { pick } from 'lodash';
import { combineReducers } from 'redux';
import { call, put, select, takeLatest } from 'redux-saga/effects';

import { api } from './api';
import { environment } from 'environments/environment';
import { CustomerAccountDetail } from 'generated/bffCustomerAccount/models';
import { ExtendedAxiosResponse } from 'helpers/apiClient';
import {
  createActionCreator,
  createActionType,
  createApiActionCreators,
  createReducer,
  RequestActionTypes,
} from 'helpers/redux/reduxHelpers';
import { EntityAccessManagementRoute } from 'helpers/routes/modules/accessManagement';
import { buildRoute } from 'helpers/routes/routeBuilder';
import { ROUTES } from 'helpers/routes/routes';
import { store } from 'helpers/store/configureStore';
import { AppState } from 'helpers/store/models/AppState';
import { ApiClientRequest } from 'models/ApiClientRequest';
import { ClearContextEntityType, ClearContextSuccessActionInputs } from 'models/context/ClearContext';
import { ContextCustomerEntities } from 'models/context/ContextCustomerEntities';
import { CustomerAccountInfo } from 'models/context/CustomerAccountInfo';
import { ExternalUser } from 'models/context/ExternalUser';
import { getApiUrl, performApiCall } from 'saga/apiClient/ducks';
import { AuthActionTypes } from 'saga/auth/ducks';
import {
  canClearContextEntityCheck,
  clearContextSuccess,
  ContextActionTypes,
  selectContextCustomerEntities,
  validateContextAction,
} from 'saga/context/ducks';
import { selectContextExternalUser } from 'saga/context/external/ducks';

/* STATE */
export interface ContextCustomerAccountState {
  customerAccount: CustomerAccountInfo | null;
}

/* ACTION TYPES */
export enum ContextCustomerAccountActionTypes {
  SetContextCustomerAccount = '@@Context/CustomerAccount/GET_CONTEXT_CUSTOMER_ACCOUNT',
  SetContextCustomerAccountSkipped = '@@Context/CustomerAccount/GET_CONTEXT_CUSTOMER_ACCOUNT_SKIPPED',
}

/* ACTIONS */
export const setContextCustomerAccountActions = createApiActionCreators(
  ContextCustomerAccountActionTypes.SetContextCustomerAccount
);
export const setContextCustomerAccountSkippedActions = createActionCreator(
  ContextCustomerAccountActionTypes.SetContextCustomerAccountSkipped
);

/* REDUCERS */
const initialState: ContextCustomerAccountState = {
  customerAccount: null,
};

const CLEAR_CONTEXT_RELATED_ENTITIES = [ClearContextEntityType.CUSTOMER];

const customerAccount = createReducer(initialState.customerAccount, {
  [ContextActionTypes.ClearContext]: {
    [RequestActionTypes.SUCCESS]: (_state: CustomerAccountInfo | null, payload: ClearContextSuccessActionInputs) =>
      canClearContextEntityCheck(payload, ClearContextEntityType.CUSTOMER_ACCOUNT, CLEAR_CONTEXT_RELATED_ENTITIES)
        ? null
        : _state,
    [RequestActionTypes.FAILURE]: (_state: CustomerAccountInfo | null) => _state,
  },
  [ContextActionTypes.ValidateContext]: {
    [RequestActionTypes.FAILURE]: (_state: CustomerAccountInfo, payload: ContextCustomerEntities) =>
      payload.customerAccount,
  },
  [ContextCustomerAccountActionTypes.SetContextCustomerAccount]: {
    [RequestActionTypes.SUCCESS]: (_state: CustomerAccountInfo | null, payload: CustomerAccountDetail) => {
      return {
        ...pick<CustomerAccountInfo>(payload, [
          'caRefNo',
          'cuRefNo',
          'parentCaRefNo',
          'payerCaRefNo',
          'customName',
          'paymentResponsible',
        ]),
        displayName: payload.customName,
      };
    },
    [RequestActionTypes.FAILURE]: () => null,
  },
  [AuthActionTypes.Logout]: {
    [RequestActionTypes.SUCCESS]: () => null,
  },
});

export default combineReducers<ContextCustomerAccountState>({
  customerAccount,
});

/* SELECTORS */
const selectContextCustomerAccount = (state: AppState) => state.context.customerAccount || {};

export const selectContextCustomerAccountInfo = (state: AppState): CustomerAccountInfo | null =>
  selectContextCustomerAccount(state).customerAccount;

/* SAGAS */
function* setContextCustomerAccount({ payload }: any) {
  if (!payload.caRefNo) {
    return;
  }

  const contextCustomerAccount: CustomerAccountInfo = yield select(selectContextCustomerAccountInfo);
  const externalUser: ExternalUser = yield select(selectContextExternalUser);

  if (payload.forceUpdate || !contextCustomerAccount || contextCustomerAccount.caRefNo !== payload.caRefNo) {
    // see detail in EA Projects.O2 Czech Republic.MO2-CARE.Processes.Common.Entities.Session (Context).setContextCustomerAccount.check isTrustedFlag for in.caRefNo
    if (
      externalUser &&
      externalUser?.entityAccesses?.find((ea) => ea.entityRefNo === payload.caRefNo && !ea.isTrusted && !ea.isVerified)
    ) {
      // redirect to WF 2.6.5
      store.dispatch(
        replace(
          `${buildRoute(ROUTES.VerifyEntityAccess)}?cpRefNo=${externalUser?.cpRefNo}&entityRefNo=${
            payload.caRefNo
          }&entityType=CustomerAccount&redirectUrl=${
            window.location.pathname === EntityAccessManagementRoute.VerifyEntityAccess
              ? new URLSearchParams(window.location.search).get('redirectUrl')
              : window.location.href
          }`
        )
      );

      return;
    }

    const request: ApiClientRequest = {
      apiCall: call(api.getCustomerAccount, yield getApiUrl(environment.urlConfig.customerAccount), payload.caRefNo),
      skipDefaultKahlBehavior: true,
    };
    const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

    if (resp.ok) {
      yield put(setContextCustomerAccountActions.success(resp.data));
    } else {
      console.log('setContextCustomerAccount failure: api.getCustomerAccount failed');
      yield put(setContextCustomerAccountActions.failure());
    }
  } else {
    console.info(
      `setContextCustomerAccount skipped: contextCustomerAccount[caRefNo=${payload.caRefNo}] already exists.`
    );
    yield put(setContextCustomerAccountSkippedActions(contextCustomerAccount));
  }
}

function* validateContextSetCuFromCa() {
  // validateContext: CU_FROM_CA
  const customerAccount: CustomerAccountInfo = yield select(selectContextCustomerAccountInfo);

  yield put(validateContextAction.request({ cuRefNo: customerAccount.cuRefNo }));
}

/**
 * EA: Projects.O2 Czech Republic.MO2-CARE.Processes.Common.Entities.Session (Context).validateContext.validateContext
 * Part: setContextCustomerAccount
 */
function* validateContextSetContextCustomerAccount({ payload }: any) {
  if (!payload.caRefNo) {
    return;
  }

  const contextEntitiesBackup: ContextCustomerEntities = yield select(selectContextCustomerEntities);

  if (payload.cleanContextFirst) {
    yield call(clearContextSuccess, ClearContextEntityType.TARIFF_SPACE);
  }

  // set context customer account
  yield put(
    setContextCustomerAccountActions.request({
      caRefNo: payload.caRefNo,
      forceUpdate: payload.forceUpdate,
    })
  );

  // waiting for context customer account to be set
  yield takeLatest(
    createActionType(ContextCustomerAccountActionTypes.SetContextCustomerAccount, RequestActionTypes.SUCCESS),
    validateContextSetCuFromCa
  );
  yield takeLatest(
    createActionType(ContextCustomerAccountActionTypes.SetContextCustomerAccountSkipped),
    validateContextSetCuFromCa
  );

  yield takeLatest(
    createActionType(ContextCustomerAccountActionTypes.SetContextCustomerAccount, RequestActionTypes.FAILURE),
    function* () {
      // validateContext: set back original context state
      console.log('validateContext failure: SetContextCustomerAccount failed - set back original context state');
      yield put(validateContextAction.failure(contextEntitiesBackup));
    }
  );
}

/* EXPORT */
export function* contextCustomerAccountSaga() {
  yield takeLatest(
    createActionType(ContextCustomerAccountActionTypes.SetContextCustomerAccount, RequestActionTypes.REQUEST),
    setContextCustomerAccount
  );

  yield takeLatest(
    createActionType(ContextActionTypes.ValidateContext, RequestActionTypes.REQUEST),
    validateContextSetContextCustomerAccount
  );
}
